import debug from 'debug';
import qs from 'qs';
import config from '../config';
import { setAuthData, getAuthData } from '../auth/session';
import { hasUsableRefreshToken } from '../auth/refreshTokenUtils';
import { updateChatbotToken } from '../integrations/chatbot';

// Create debug namespaces for token authentication operations
const debugToken = debug('auth:tokenAuth');
const debugError = debug('auth:tokenAuth:error');

/**
 * Fetch wrapper with timeout support for token endpoints
 * Uses native fetch instead of axios to avoid ESLint restrictions
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @returns {Promise<Response>}
 */
import { EXTERNAL_REFRESH_TIMEOUT_MS } from '../config/auth.config';

const fetchWithTimeout = (url, options = {}, timeout = EXTERNAL_REFRESH_TIMEOUT_MS) => {
  debugToken('fetchWithTimeout: %s (timeout: %dms)', url, timeout);

  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      debugError('Request timeout after %dms for %s', timeout, url);
      reject(new Error('Request timeout'));
    }, timeout);
  });

  return Promise.race([
    fetch(url, options),
    timeoutPromise,
  ]).finally(() => {
    // Clear timeout to prevent memory leak and spurious error logs
    if (timeoutId) {
      clearTimeout(timeoutId);
      debugToken('fetchWithTimeout: cleared timeout for %s', url);
    }
  });
};

/**
 * Sanitizes OAuth2 error response to prevent information disclosure
 * Only preserves standard RFC 6749 Section 5.2 error fields
 *
 * @param {object} errorData - Raw error response from OAuth2 endpoint
 * @returns {object} Sanitized error object with only safe fields
 */
const sanitizeOAuthError = (errorData) => {
  // Only preserve OAuth2 standard error fields (RFC 6749 Section 5.2)
  const safeFields = {
    error: errorData?.error,  // Standard error code (invalid_grant, invalid_request, etc.)
    error_description: errorData?.error_description,  // User-safe description
    error_uri: errorData?.error_uri,  // Documentation URL
  };

  // Remove null/undefined fields
  return Object.fromEntries(
    Object.entries(safeFields).filter(([_, v]) => v != null)
  );
};

const TOKEN_ENDPOINT_HEADERS = {
  'content-type': 'application/x-www-form-urlencoded',
};

const fetchOAuth2Token = async ({
  payload,
  context = 'tokenAuth',
  includeStatusMeta = false,
  sanitizeErrors = true,
  onBeforeFetch = () => { },
  onSuccess = () => { },
  onError = () => { },
}) => {
  onBeforeFetch(payload);
  debugToken('%s: Calling token endpoint', context);

  let response;
  try {
    response = await fetchWithTimeout(config.tokenUrl, {
      method: 'POST',
      headers: TOKEN_ENDPOINT_HEADERS,
      body: qs.stringify(payload),
    });
  } catch (networkError) {
    debugError('%s: Network or timeout error - %O', context, networkError);
    onError(networkError);
    throw networkError;
  }

  if (!response.ok) {
    debugError('%s: HTTP error - status: %d', context, response.status);
    let errorPayload;

    try {
      errorPayload = await response.json();
    } catch (parseError) {
      debugError('%s: Failed to parse error response - %O', context, parseError);
    }

    const error = new Error(`HTTP error! status: ${response.status}`);
    error.response = {
      status: response.status,
    };

    if (includeStatusMeta) {
      error.response.statusText = response.statusText;
    }

    if (errorPayload !== undefined) {
      error.response.data = sanitizeErrors
        ? sanitizeOAuthError(errorPayload)
        : errorPayload;
    } else if (sanitizeErrors) {
      error.response.data = {};
    }

    onError(error, errorPayload);
    throw error;
  }

  debugToken('%s: Response received, parsing JSON', context);
  const data = await response.json();
  onSuccess(data);

  if (includeStatusMeta) {
    return {
      data,
      status: response.status,
      statusText: response.statusText,
    };
  }

  return data;
};

export const refreshToken = async () => {
  debugToken('refreshToken: Starting token refresh');
  const authObject = getAuthData();

  if (!hasUsableRefreshToken(authObject?.refreshToken)) {
    debugError('refreshToken: No refresh token found in auth data');
    throw new Error('refresh token not found');
  }

  const refreshData = {
    grant_type: 'refresh_token',
    client_id: config.AuthorizationClientId,
    refresh_token: authObject.refreshToken,
  };

  try {
    const data = await fetchOAuth2Token({
      payload: refreshData,
      context: 'refreshToken',
    });

    debugToken('refreshToken: Token refresh successful, updating auth data');
    debugToken('refreshToken: response data structure - %O', {
      hasAccessToken: !!data.access_token,
      hasRefreshToken: !!data.refresh_token,
      accessTokenType: typeof data.access_token,
      refreshTokenType: typeof data.refresh_token,
      accessTokenLength: data.access_token?.length,
      refreshTokenLength: data.refresh_token?.length,
      dataKeys: Object.keys(data)
    });

    // Use new refresh token if provided, otherwise keep the existing one (defensive)
    // Note: Refresh tokens are simple hex strings (e.g., "b9872abdd57c43cf8ea420443f4a74cf"), not JWTs
    const newRefreshToken = data.refresh_token || authObject.refreshToken;
    debugToken('refreshToken: using refresh token - %s',
      data.refresh_token ? 'new from API' : 'existing from storage');

    setAuthData(
      data.access_token,
      authObject.unique_name,
      newRefreshToken,
      authObject.userImpersonation,
    );
    updateChatbotToken(data.access_token);
    debugToken('refreshToken: Auth data and chatbot token updated');
    return data;
  } catch (error) {
    debugError('refreshToken: Failed - %O', error);
    throw error;
  }
};

export const impersonateByRefreshToken = async (refreshTokenValue, userName) => {
  debugToken('impersonateByRefreshToken: Starting impersonation for user: %s', userName);

  const refreshData = {
    grant_type: 'refresh_token',
    client_id: config.AuthorizationClientId,
    refresh_token: refreshTokenValue,
  };

  try {
    const data = await fetchOAuth2Token({
      payload: refreshData,
      context: 'impersonateByRefreshToken',
    });

    debugToken('impersonateByRefreshToken: Success, setting auth data for user: %s', userName);
    setAuthData(data.access_token, userName, data.refresh_token, true);
    updateChatbotToken(data.access_token);
    debugToken('impersonateByRefreshToken: Impersonation complete');
    return data;
  } catch (error) {
    debugError('impersonateByRefreshToken: Failed for user %s - %O', userName, error);
    throw error;
  }
};


export const login = async (userName, password, isWindowsAuthentication) => {
  const authentication_type = isWindowsAuthentication ? 'Windows' : 'Individual';
  debugToken('login: Starting login for user: %s (auth type: %s)', userName, authentication_type);

  const loginData = {
    grant_type: 'password',
    username: userName,
    password,
    authentication_type,
    client_id: config.AuthorizationClientId,
  };

  try {
    const response = await fetchOAuth2Token({
      payload: loginData,
      context: `login (${userName})`,
      includeStatusMeta: true,
    });

    debugToken('login: Login successful for user: %s, setting auth data', userName);
    debugToken('login: response data structure - %O', {
      hasData: !!response.data,
      dataKeys: response.data ? Object.keys(response.data) : [],
      hasAccessToken: !!response.data?.access_token,
      hasRefreshToken: !!response.data?.refresh_token,
      accessTokenType: typeof response.data?.access_token,
      refreshTokenType: typeof response.data?.refresh_token,
      accessTokenLength: response.data?.access_token?.length,
      refreshTokenLength: response.data?.refresh_token?.length
    });
    setAuthData(response.data.access_token, userName, response.data.refresh_token, false);
    updateChatbotToken(response.data.access_token);
    debugToken('login: Auth data and chatbot token updated');
    return response;
  } catch (error) {
    debugError('login: Login failed for user: %s - %O', userName, error);
    throw error;
  }
};
